import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Alert,
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  FileDownload as ExportIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import {
  DataGrid,
  GridColDef,
  GridActionsCellItem,
  GridRowParams,
} from '@mui/x-data-grid';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { fetchOperatingSupplies, deleteOperatingSupply } from '../../store/slices/inventorySlice';
import { fetchMachines } from '../../store/slices/machinesSlice';
import { OperatingSupply } from '../../types/inventory';
import { LowStockIndicator, filterLowStockItems } from '../../components/inventory/LowStockIndicator';
import { format, parseISO, differenceInDays, isPast } from 'date-fns';
import { de } from 'date-fns/locale';

const isExpiringSoon = (expiryDate: string | undefined, days: number = 30): boolean => {
  if (!expiryDate) return false;
  try {
    const expiry = parseISO(expiryDate);
    const daysUntilExpiry = differenceInDays(expiry, new Date());
    return daysUntilExpiry >= 0 && daysUntilExpiry <= days;
  } catch {
    return false;
  }
};

const isExpired = (expiryDate: string | undefined): boolean => {
  if (!expiryDate) return false;
  try {
    return isPast(parseISO(expiryDate));
  } catch {
    return false;
  }
};

export const OperatingSuppliesOverviewPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { items: supplies, isLoading } = useAppSelector((state) => state.inventory.operatingSupplies);
  const { machines } = useAppSelector((state) => state.machines);
  const { user } = useAppSelector((state) => state.auth);

  const [searchQuery, setSearchQuery] = useState('');
  const [machineFilter, setMachineFilter] = useState<string>('all');
  const [showLowStockOnly, setShowLowStockOnly] = useState(false);
  const [showExpiringOnly, setShowExpiringOnly] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<OperatingSupply | null>(null);

  const canEdit = user?.role === 'admin';

  useEffect(() => {
    dispatch(fetchMachines({ page: 1, limit: 1000 }));
  }, [dispatch]);

  useEffect(() => {
    if (machines.length > 0) {
      machines.forEach(machine => {
        dispatch(fetchOperatingSupplies({ machineId: machine.id }));
      });
    }
  }, [dispatch, machines]);

  const handleRefresh = () => {
    machines.forEach(machine => {
      dispatch(fetchOperatingSupplies({ machineId: machine.id }));
    });
  };

  const handleEditItem = (item: OperatingSupply) => {
    navigate(`/machines/${item.machine_id}?tab=supplies`);
  };

  const handleDeleteRequest = (item: OperatingSupply) => {
    setSelectedItem(item);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (selectedItem) {
      await dispatch(deleteOperatingSupply(selectedItem.id));
      setDeleteDialogOpen(false);
      setSelectedItem(null);
      handleRefresh();
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setSelectedItem(null);
  };

  const getMachineName = (machineId: string): string => {
    const machine = machines.find(m => m.id === machineId);
    return machine?.name || 'Unknown';
  };

  const handleExport = () => {
    const csvContent = [
      ['Name', 'Machine', 'Quantity', 'Min Quantity', 'Unit', 'Supplier', 'Price', 'Expiry Date'].join(','),
      ...filteredSupplies.map(item => [
        `"${item.name}"`,
        `"${getMachineName(item.machine_id)}"`,
        item.quantity,
        item.min_quantity,
        item.unit,
        item.supplier || '',
        item.price || '',
        item.expiry_date ? format(parseISO(item.expiry_date), 'dd.MM.yyyy') : ''
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `betriebsmittel_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  let filteredSupplies = supplies.filter(item => {
    if (!item) return false;

    const matchesSearch = !searchQuery ||
      item.name.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesMachine = machineFilter === 'all' || item.machine_id === machineFilter;

    return matchesSearch && matchesMachine;
  });

  if (showLowStockOnly) {
    filteredSupplies = filterLowStockItems(filteredSupplies);
  }

  if (showExpiringOnly) {
    filteredSupplies = filteredSupplies.filter(s => isExpiringSoon(s.expiry_date, 30));
  }

  const lowStockCount = filterLowStockItems(supplies).length;
  const expiringCount = supplies.filter(s => isExpiringSoon(s.expiry_date, 30)).length;

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Name',
      flex: 1,
      minWidth: 200,
    },
    {
      field: 'machine_id',
      headerName: 'Maschine',
      width: 180,
      valueGetter: (value: any, row: any) => getMachineName(row.machine_id),
    },
    {
      field: 'quantity',
      headerName: 'Bestand',
      width: 150,
      renderCell: (params) => (
        <LowStockIndicator
          quantity={params.row.quantity}
          minQuantity={params.row.min_quantity}
          unit={params.row.unit}
          inline
        />
      ),
    },
    {
      field: 'unit',
      headerName: 'Einheit',
      width: 90,
    },
    {
      field: 'supplier',
      headerName: 'Lieferant',
      width: 150,
      valueFormatter: (value: any) => value || '-',
    },
    {
      field: 'expiry_date',
      headerName: 'Ablaufdatum',
      width: 150,
      renderCell: (params) => {
        if (!params.value) return '-';
        const expired = isExpired(params.value);
        const expiringSoon = isExpiringSoon(params.value, 30);

        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {(expired || expiringSoon) && <WarningIcon fontSize="small" color={expired ? 'error' : 'warning'} />}
            <Typography
              variant="body2"
              color={expired ? 'error' : expiringSoon ? 'warning.main' : 'text.primary'}
              sx={{ fontWeight: expired || expiringSoon ? 600 : 400 }}
            >
              {format(parseISO(params.value), 'dd.MM.yyyy', { locale: de })}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'price',
      headerName: 'Preis',
      width: 100,
      valueFormatter: (value: any) => value ? `€${value.toFixed(2)}` : '-',
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Aktionen',
      width: 100,
      getActions: (params: GridRowParams) => {
        const actions = [
          <GridActionsCellItem
            icon={<EditIcon />}
            label="Bearbeiten"
            onClick={() => handleEditItem(params.row as OperatingSupply)}
            disabled={!canEdit}
          />,
        ];
        if (canEdit) {
          actions.push(
            <GridActionsCellItem
              icon={<DeleteIcon />}
              label="Löschen"
              onClick={() => handleDeleteRequest(params.row as OperatingSupply)}
            />
          );
        }
        return actions;
      },
    },
  ];

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Betriebsmittel Übersicht
        </Typography>
        <Box display="flex" gap={1}>
          <Button
            variant="outlined"
            startIcon={<ExportIcon />}
            onClick={handleExport}
            disabled={filteredSupplies.length === 0}
          >
            Export CSV
          </Button>
          <IconButton onClick={handleRefresh} title="Aktualisieren">
            <RefreshIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Alerts */}
      {lowStockCount > 0 && !showLowStockOnly && (
        <Alert severity="warning" sx={{ mb: 1 }}>
          {lowStockCount} Betriebsmittel mit niedrigem Bestand
        </Alert>
      )}
      {expiringCount > 0 && !showExpiringOnly && (
        <Alert severity="warning" sx={{ mb: 1 }}>
          {expiringCount} Betriebsmittel laufen in den nächsten 30 Tagen ab
        </Alert>
      )}

      {/* Filters */}
      <Box sx={{ mb: 3, mt: 2 }}>
        <Box display="flex" gap={2} flexWrap="wrap">
          <TextField
            placeholder="Betriebsmittel durchsuchen..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ minWidth: 300 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Maschine</InputLabel>
            <Select
              value={machineFilter}
              label="Maschine"
              onChange={(e) => setMachineFilter(e.target.value)}
            >
              <MenuItem value="all">Alle Maschinen</MenuItem>
              {machines.map((machine) => (
                <MenuItem key={machine.id} value={machine.id}>
                  {machine.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant={showLowStockOnly ? 'contained' : 'outlined'}
            onClick={() => {
              setShowLowStockOnly(!showLowStockOnly);
              if (!showLowStockOnly) setShowExpiringOnly(false);
            }}
          >
            Niedriger Bestand
          </Button>
          <Button
            variant={showExpiringOnly ? 'contained' : 'outlined'}
            onClick={() => {
              setShowExpiringOnly(!showExpiringOnly);
              if (!showExpiringOnly) setShowLowStockOnly(false);
            }}
          >
            Läuft ab
          </Button>
        </Box>
      </Box>

      {/* Summary */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          {filteredSupplies.length} von {supplies.length} Betriebsmittel
        </Typography>
      </Box>

      {/* Data Grid */}
      <Box sx={{ height: 600, width: '100%' }}>
        <DataGrid
          rows={filteredSupplies}
          columns={columns}
          loading={isLoading}
          disableRowSelectionOnClick
          pageSizeOptions={[10, 25, 50, 100]}
          initialState={{
            pagination: { paginationModel: { pageSize: 25 } },
          }}
        />
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
        <DialogTitle>Betriebsmittel löschen</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Möchten Sie "{selectedItem?.name}" wirklich löschen?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Abbrechen</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Löschen
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
